import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaBolt,
  FaPaperPlane,
} from "react-icons/fa";
import { db, collection, addDoc } from "../firebase.js";
import emailjs from "@emailjs/browser";

const EnquiryPage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // VALIDATION
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = "Name is required";

    if (!formData.email.trim()) errors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email))
      errors.email = "Invalid email";

    if (!formData.company.trim())
      errors.company = "Company name is required";

    if (!formData.message.trim())
      errors.message = "Please describe your requirement";

    return Object.keys(errors).length === 0;
  };

  // HANDLE INPUT
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      // FIRESTORE
      await addDoc(collection(db, "enquiries"), {
        ...formData,
        timestamp: new Date().toISOString(),
      });

      // EMAILS
      try {
        // CUSTOMER EMAIL
        await emailjs.send(
          "service_4csa82e",
          "template_dgq65xu",
          {
            name: formData.name,
            email: formData.email,
            company: formData.company,
            phone: formData.phone,
            message: formData.message,
            time: new Date().toLocaleString(),
          },
          "v-3WMZ6Rafl3VBiyU"
        );

        // ADMIN EMAIL
        await emailjs.send(
          "service_4csa82e",
          "template_iy47i6g",
          {
            name: formData.name,
            email: formData.email,
            company: formData.company,
            phone: formData.phone,
            message: formData.message,
            time: new Date().toLocaleString(),
          },
          "v-3WMZ6Rafl3VBiyU"
        );

      } catch (emailError) {
        console.error("Email error:", emailError);
      }

      // SUCCESS
      setSubmitted(true);

      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        message: "",
      });

    } catch (error) {
      console.error("Firestore error:", error);

      setSubmitError(
        "Failed to submit enquiry. Please try again."
      );

    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden text-slate-200">

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0b0f19] to-[#111827] opacity-95"></div>

      {/* Glow Waves */}
      <div className="absolute inset-0 pointer-events-none opacity-40">

        <div
          className="absolute top-0 left-0 w-[120%] h-[120%]
          bg-gradient-to-r from-orange-500/30 via-yellow-400/20 to-sky-500/30
          blur-3xl animate-slowWave"
        ></div>

        <div
          className="absolute bottom-0 right-0 w-[120%] h-[120%]
          bg-gradient-to-r from-sky-500/20 via-orange-300/20 to-yellow-400/30
          blur-3xl animate-fastWave"
        ></div>

      </div>

      {/* Grid Overlay */}
      <div
        className="absolute inset-0 pointer-events-none
        bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),
             linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)]
        bg-[size:60px_60px]"
      ></div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(18)].map((_, i) => (
          <span
            key={i}
            className="absolute w-1 h-1 bg-white rounded-full opacity-20 animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${2 + Math.random() * 4}s`,
            }}
          ></span>
        ))}
      </div>

      {/* MAIN */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 py-20">

        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="group flex items-center text-yellow-400 hover:text-yellow-300 mb-10 transition-all duration-300"
        >
          <FaArrowLeft className="mr-3 transition-transform duration-300 group-hover:-translate-x-1" />
          Back to Home
        </button>

        {/* HEADER */}
        <div className="text-center mb-14">

          <div className="inline-flex items-center px-5 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-xl mb-5">
            <FaBolt className="text-yellow-400 mr-2" />
            <span className="text-[11px] tracking-[4px] uppercase text-slate-300">
              Power Sense Enquiry
            </span>
          </div>

          <h1
            className="text-4xl md:text-6xl font-black mb-5
            bg-gradient-to-r from-orange-300 via-yellow-200 to-cyan-300
            text-transparent bg-clip-text
            animate-gradientMove bg-[length:200%_200%]"
          >
            Let’s Build Smarter Energy Solutions
          </h1>

          <p className="text-slate-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Connect with Power Sense for intelligent energy monitoring,
            IoT automation, analytics, smart infrastructure solutions,
            and enterprise deployments.
          </p>

        </div>

        {/* SUCCESS */}
        {submitted ? (

          <div
            className="relative overflow-hidden max-w-3xl mx-auto
            bg-gradient-to-br from-[#081b16]/90 via-[#0d241d]/80 to-[#0f172a]/90
            border border-green-500/20
            backdrop-blur-2xl rounded-3xl p-10 text-center"
          >

            {/* Glow */}
            <div className="absolute inset-0 bg-green-500/10 blur-3xl"></div>

            <div
              className="relative z-10 w-20 h-20 mx-auto mb-6
              rounded-full bg-green-500/20
              flex items-center justify-center
              border border-green-400/30"
            >
              <FaCheck className="text-green-400 text-4xl" />
            </div>

            <h2 className="relative z-10 text-3xl font-bold text-white mb-4">
              Enquiry Submitted Successfully
            </h2>

            <p className="relative z-10 text-slate-300 mb-8 text-lg">
              Thank you for contacting Power Sense.
              Our team will reach out shortly.
            </p>

            <button
              onClick={() => navigate("/")}
              className="relative z-10 px-8 py-4 rounded-2xl
              bg-gradient-to-r from-orange-500 to-yellow-400
              text-black font-semibold
              hover:scale-105 transition-all duration-500
              shadow-2xl"
            >
              Return to Home
            </button>

          </div>

        ) : (

          /* FORM */
          <div
            className="relative overflow-hidden
            bg-[#121826]/70 backdrop-blur-2xl
            border border-white/10
            rounded-[2rem] p-8 md:p-10
            shadow-[0_20px_80px_rgba(0,0,0,0.45)]"
          >

            {/* Hover Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 via-orange-500/5 to-sky-500/10 blur-3xl"></div>

            {/* Top Border */}
            <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-orange-500 via-yellow-400 to-sky-500"></div>

            <form
              onSubmit={handleSubmit}
              className="relative z-10"
            >

              {/* GRID */}
              <div className="grid md:grid-cols-2 gap-6">

                {/* NAME */}
                <div className="group">
                  <label className="text-sm text-slate-300 mb-2 block">
                    Full Name
                  </label>

                  <input
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10
                    focus:border-yellow-400/40
                    focus:bg-black/30
                    text-white px-5 py-4 rounded-2xl
                    outline-none transition-all duration-300
                    placeholder:text-slate-500"
                  />
                </div>

                {/* EMAIL */}
                <div className="group">
                  <label className="text-sm text-slate-300 mb-2 block">
                    Email Address
                  </label>

                  <input
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10
                    focus:border-sky-400/40
                    focus:bg-black/30
                    text-white px-5 py-4 rounded-2xl
                    outline-none transition-all duration-300
                    placeholder:text-slate-500"
                  />
                </div>

                {/* COMPANY */}
                <div className="group">
                  <label className="text-sm text-slate-300 mb-2 block">
                    Company / Institution
                  </label>

                  <input
                    name="company"
                    placeholder="Organization name"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10
                    focus:border-orange-400/40
                    focus:bg-black/30
                    text-white px-5 py-4 rounded-2xl
                    outline-none transition-all duration-300
                    placeholder:text-slate-500"
                  />
                </div>

                {/* PHONE */}
                <div className="group">
                  <label className="text-sm text-slate-300 mb-2 block">
                    Phone Number
                  </label>

                  <input
                    name="phone"
                    placeholder="Optional"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-black/20 border border-white/10
                    focus:border-green-400/40
                    focus:bg-black/30
                    text-white px-5 py-4 rounded-2xl
                    outline-none transition-all duration-300
                    placeholder:text-slate-500"
                  />
                </div>

              </div>

              {/* MESSAGE */}
              <div className="mt-6">

                <label className="text-sm text-slate-300 mb-2 block">
                  Describe Your Requirement
                </label>

                <textarea
                  name="message"
                  rows="5"
                  placeholder="Tell us about your energy monitoring or IoT automation needs..."
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full bg-black/20 border border-white/10
                  focus:border-yellow-400/40
                  focus:bg-black/30
                  text-white px-5 py-4 rounded-2xl
                  outline-none transition-all duration-300
                  placeholder:text-slate-500 resize-none"
                />
              </div>

              {/* ERROR */}
              {submitError && (
                <p className="text-red-400 mt-5 text-center">
                  {submitError}
                </p>
              )}

              {/* BUTTON */}
              <div className="text-center mt-10">

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative overflow-hidden
                  px-10 py-4 rounded-2xl
                  bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-500
                  text-black font-bold
                  hover:scale-105 transition-all duration-500
                  shadow-[0_10px_40px_rgba(255,196,0,0.25)]"
                >

                  <span className="relative z-10 flex items-center">

                    {submitting
                      ? "Submitting..."
                      : "Send Enquiry"}

                    <FaPaperPlane
                      className="ml-3 transition-transform duration-500
                      group-hover:translate-x-1 group-hover:-translate-y-1"
                    />

                  </span>

                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-20
                    transition-all duration-700
                    bg-white blur-2xl"
                  ></div>

                </button>

              </div>

            </form>
          </div>
        )}

      </div>

      {/* Animations */}
      <style>{`

        @keyframes slowWave {
          0% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
          50% {
            transform: translate(10%, 10%) rotate(14deg);
          }
          100% {
            transform: translate(-10%, -10%) rotate(0deg);
          }
        }

        @keyframes fastWave {
          0% {
            transform: translate(10%, 10%) rotate(0deg);
          }
          50% {
            transform: translate(-15%, -10%) rotate(-14deg);
          }
          100% {
            transform: translate(10%, 10%) rotate(0deg);
          }
        }

        @keyframes gradientMove {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-gradientMove {
          animation: gradientMove 6s ease infinite;
        }

        .animate-slowWave {
          animation: slowWave 14s ease-in-out infinite;
        }

        .animate-fastWave {
          animation: fastWave 10s ease-in-out infinite;
        }

      `}</style>

    </div>
  );
};

export default EnquiryPage;